import StickyFooterAtomic from 'components/atomic/organisms/sticky-footer';
import PrimaryActionButton from 'components/atomic/molecules/primary-action-button';
import CircularIconButton from 'components/atomic/molecules/circular-icon-button';
import ShareIcon from 'components/icon/share-icon';
import { HeartIcon, HeartOutlineIcon } from 'components/atomic/atoms/icons';
import { useListingStickyFooter } from './use-listing-sticky-footer';
import type { ListingStickyFooterInput } from './use-listing-sticky-footer';

export interface ListingStickyFooterProps extends ListingStickyFooterInput {
  /** Additional CSS class */
  className?: string;
}

/**
 * ListingStickyFooter - Mobile action bar for listing pages
 * 
 * UI component that composes the atomic StickyFooter with listing-specific
 * business logic (i18n, first-visit detection).
 * 
 * Features:
 * - Dynamic CTA based on user type (first-time vs return)
 * - Share and favorite actions
 * - Fixed positioning with iOS safe area support
 * 
 * @example
 * ```tsx
 * <ListingStickyFooter
 *   onRequestInfo={() => openPVR(false)}
 *   onBookShowing={() => openPVR(true)}
 *   share={{ text: shareText, url: shareUrl }}
 *   favorite={{ isFavorited: false, onToggle: toggleFavorite }}
 * />
 * ```
 */
export function ListingStickyFooter({
  onRequestInfo,
  onBookShowing,
  isComingSoon,
  share,
  favorite,
  className,
}: ListingStickyFooterProps) {
  const {
    primaryAction,
    shareLabel,
    favoriteLabel,
    handleShare,
    handleFavorite,
  } = useListingStickyFooter({ onRequestInfo, onBookShowing, isComingSoon, share, favorite });

  return (
    <div data-testid="ui-listing-sticky-footer">
      <StickyFooterAtomic
        className={className}
        primaryAction={
        <PrimaryActionButton
          label={primaryAction.label}
          onClick={primaryAction.onClick}
        />
      }
      secondaryActions={
        <>
          {share && (
            <CircularIconButton
              icon={<ShareIcon />}
              onClick={handleShare}
              ariaLabel={shareLabel}
            />
          )}

          {favorite && (
            <CircularIconButton
              icon={favorite.isFavorited ? <HeartIcon /> : <HeartOutlineIcon />}
              onClick={handleFavorite}
              ariaLabel={favoriteLabel}
              isActive={favorite.isFavorited}
            />
          )}
        </>
      }
    />
    </div>
  );
}

export default ListingStickyFooter;
